/* eslint-env browser */
import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FirebaseContext, api } from 'common';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { cellOf } from 'common/src/other/demandHeatCodec';
import addisZonesJson from 'common/src/other/addisZones.json';
import {
  Box, Paper, Typography, Tabs, Tab, Button, IconButton, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Autocomplete,
  ToggleButton, ToggleButtonGroup, Stack, Tooltip, CircularProgress, Divider, Switch, Collapse,
  Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReplayIcon from '@mui/icons-material/Replay';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LinkIcon from '@mui/icons-material/Link';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import GoogleMapsAutoComplete from '../components/GoogleMapsAutoComplete';
import DashboardCard from '../components/DashboardCard';
import { confirmDialog, showToast, InlineMessage } from '../components/feedback';
import { AddisDateTimeField, FieldLabel, StatusChip } from '../components/modern';
import { formatAddisTime, ADDIS_OFFSET_MS } from '../common/exportUtils';
import { tokens as c } from '../theme';
import useMediaQuery from '@mui/material/useMediaQuery';

// Dialogs go full screen on a phone, so the keyboard never covers the
// focused field or the dialog's buttons (owner rule).
const usePhone = () => useMediaQuery((theme) => theme.breakpoints.down('sm'));

// City events — crowd events (concerts, matches, services, holidays) the call
// centre enters and supervisors publish so drivers can position for the
// crowd. Phase 1 (this file): list, create / edit, submit → approve flow.
// Data contract + rules: common/src/actions/cityeventsactions.js and
// CITY_EVENTS.md. Reads are one-shot (Refresh button), never a listener.
//
// Who sees what: supervisors and admins see every event; agents see every
// PUBLISHED event plus their own drafts / pending / rejected ones. Agents can
// only save drafts and submit; publishing, rejecting, unpublishing and
// cancelling are supervisor actions (the RTDB rules enforce the same).
// Phase 4 adds the admin-only Auto events tab, phase 5 the Impact tab
// (supervisors + admins): the report the nightly writes under each event.

const STATUS = api.CITY_EVENT_STATUS;
const CATEGORIES = api.CITY_EVENT_CATEGORIES;
const WINDOW_DEFAULTS = api.CITY_EVENT_WINDOW_DEFAULTS;
const ADDIS_CENTER = { lat: 9.0108, lng: 38.7613 };
const PLACES = (addisZonesJson && addisZonesJson.zones ? addisZonesJson.zones : [])
  .map((z, i) => ({ key: 'p' + i, en: z.en, am: z.am, lat: Number(z.lat), lng: Number(z.lng) }))
  .filter((z) => z.en && Number.isFinite(z.lat) && Number.isFinite(z.lng));
const POSTER_MAX_BYTES = 300 * 1024;
const POSTER_MAX_SIDE = 1280;
const ROWS_PER_PAGE = 10;

// Status as an icon and a word on a tint (never colour alone).
const STATUS_TONE = {
  draft: { tone: 'neutral', icon: <EditNoteOutlinedIcon /> },
  pending: { tone: 'warning', icon: <ScheduleRoundedIcon /> },
  published: { tone: 'success', icon: <CheckCircleOutlineRoundedIcon /> },
  rejected: { tone: 'error', icon: <BlockRoundedIcon /> },
  cancelled: { tone: 'neutral', icon: <EventBusyOutlinedIcon /> },
};
const statusChip = (status, label) => {
  const s = STATUS_TONE[status] || { tone: 'neutral', icon: null };
  return <StatusChip tone={s.tone} icon={s.icon} label={label} />;
};
// HOUSE RULE (owner, 2026-10-15): every time on screen is Addis Ababa time
// (UTC+3, no DST) on a 12-hour clock, whatever the browser's locale or zone.
// Storage stays absolute ms; the helpers below pin the display, the entry
// field (components/modern AddisDateTimeField) pins the input (a native
// datetime-local would follow the OS clock).
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const addisDate = (ms) => new Date(Number(ms) + ADDIS_OFFSET_MS);
const fmtShortDay = (ms) => { const d = addisDate(ms); return d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]; }; // "25 Oct"
const fmtDay = (ms) => WEEKDAYS[addisDate(ms).getUTCDay()] + ' ' + fmtShortDay(ms); // "Sun 25 Oct"
const fmtTime = (ms) => formatAddisTime(Number(ms)); // "2:31 pm"
const fmtHour = (ms) => { const h = addisDate(ms).getUTCHours(); return (h % 12 === 0 ? 12 : h % 12) + ' ' + (h < 12 ? 'am' : 'pm'); }; // "2 pm"
const fmtDayTime = (ms) => fmtDay(ms) + ', ' + fmtTime(ms); // "Sun 25 Oct, 2:31 pm"
const fmtWhen = (ev) => (api.addisDayKey(Number(ev.start)) === api.addisDayKey(Number(ev.end))
  ? fmtDay(ev.start) + ' · ' + fmtTime(ev.start) + ' – ' + fmtTime(ev.end)
  : fmtDay(ev.start) + ' ' + fmtTime(ev.start) + ' → ' + fmtDay(ev.end) + ' ' + fmtTime(ev.end));

// Date + hour + minute + am/pm, all in Addis time; emits absolute ms (the
// portal's shared field: the same parts ↔ ms rule as api.msToAddisParts).
function AddisTimeField({ id, label, value, onChange }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <AddisDateTimeField id={id} value={value} onChange={onChange} />
    </Box>
  );
}
const haversineKm = (a, b, c, d) => {
  const R = 6371.0088; const toRad = (x) => x * Math.PI / 180;
  const dLat = toRad(c - a); const dLng = toRad(d - b);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const nearestPlace = (lat, lng) => {
  let best = null; let bestKm = Infinity;
  PLACES.forEach((p) => { const km = haversineKm(lat, lng, p.lat, p.lng); if (km < bestKm) { bestKm = km; best = p; } });
  return best && bestKm <= 1.2 ? best : null;
};
const num = (v, def) => { const n = Number(v); return Number.isFinite(n) ? n : def; };
const hasCoord = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const clone = (o) => JSON.parse(JSON.stringify(o));

// Canvas JPEG compression: longest side ≤ 1280 px, quality stepped down until
// the blob is ≤ 300 KB. Runs in the browser; nothing is uploaded until Save.
const compressImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, POSTER_MAX_SIDE / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    let q = 0.85;
    const attempt = () => canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('compress failed')); return; }
      if (blob.size <= POSTER_MAX_BYTES || q <= 0.4) resolve(blob); else { q -= 0.1; attempt(); }
    }, 'image/jpeg', q);
    attempt();
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('not an image')); };
  img.src = url;
});

// The Maps JS API is loaded once by App.js (useJsApiLoader); views just need
// to wait for the core library before mounting a <GoogleMap>. The bootstrap
// defines window.google.maps (a stub with importLibrary) BEFORE the core
// classes exist, so the check is google.maps.Map, not google.maps.
const mapsLoaded = () => Boolean(window.google && window.google.maps && window.google.maps.Map);
const useMapsReady = () => {
  const [ready, setReady] = useState(mapsLoaded);
  useEffect(() => {
    if (ready) return undefined;
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      if (mapsLoaded()) { setReady(true); clearInterval(id); } else if (n > 40) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [ready]);
  return ready;
};

const nextFullHour = (plusHours) => { const d = new Date(); d.setMinutes(0, 0, 0); return d.getTime() + (plusHours || 0) * 3600e3; };
const emptyEvent = () => ({
  title: { en: '', am: '' },
  category: 'concert',
  venue: { name: '', nameAm: '', lat: null, lng: null, placeKey: null },
  start: nextFullHour(24),
  end: nextFullHour(27),
  size: 2,
  windows: clone(WINDOW_DEFAULTS.concert),
  image: null,
  notes: '',
  sourceUrl: '',
  source: { kind: 'manual', ref: '', feed: '' },
  status: STATUS.DRAFT,
});

export default function CityEvents() {
  const { t } = useTranslation();
  const phone = usePhone();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const cityeventsdata = useSelector((state) => state.cityeventsdata);
  const profile = auth.profile || {};
  const uid = profile.uid;
  const role = profile.usertype;
  const canApprove = role === 'admin' || role === 'callcentersupervisor';
  const isStaff = canApprove || role === 'callcenteragent';
  const isAdmin = role === 'admin'; // Auto events (sources, feeds, runs) is admin only
  const myName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email || uid || '';
  const enabled = settings && settings.cityEventsEnabled === true;

  const [tab, setTab] = useState('upcoming');
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(null); // { form, previous }
  // Short news goes to the portal's toasts (the same { severity, msg } calls as before).
  const setSnack = useCallback((sn) => {
    if (sn && sn.msg) showToast({ message: sn.msg, kind: sn.severity });
  }, []);
  const [reject, setReject] = useState(null); // { ev, reason }
  const [busyId, setBusyId] = useState(null);

  const list = useMemo(() => cityeventsdata.list || [], [cityeventsdata.list]);
  const refresh = useCallback(() => dispatch(api.fetchCityEvents({ fromMs: Date.now() - 14 * 864e5, limit: 400 })), [dispatch]);
  useEffect(() => { if (isStaff) refresh(); }, [isStaff, refresh]);

  const visible = useMemo(() => list.filter((ev) => canApprove || ev.status === STATUS.PUBLISHED || ev.createdBy === uid), [list, canApprove, uid]);
  const now = Date.now();
  const rows = useMemo(() => {
    if (tab === 'queue') return visible.filter((ev) => ev.status === STATUS.PENDING);
    if (tab === 'past') return visible.filter((ev) => Number(ev.end) < now).slice().reverse();
    return visible.filter((ev) => Number(ev.end) >= now && ev.status !== STATUS.PENDING);
  }, [visible, tab, now]);
  const queueCount = useMemo(() => visible.filter((ev) => ev.status === STATUS.PENDING).length, [visible]);
  useEffect(() => { setPage(0); }, [tab]);
  // Clamp the pager when rows shrink (an approval moves a row out of the queue).
  const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / ROWS_PER_PAGE) - 1));

  const statusLabel = (s) => t('city_events_status_' + s, { draft: 'Draft', pending: 'Pending approval', published: 'Published', rejected: 'Rejected', cancelled: 'Cancelled' }[s] || s);
  const sizeLabel = (n) => t('city_events_size_' + n, { 1: 'Hundreds', 2: 'Thousands', 3: 'Tens of thousands' }[n] || String(n));
  const categoryLabel = (c) => t('city_events_cat_' + c, c ? c.charAt(0).toUpperCase() + c.slice(1) : '');
  const sourceLabel = (ev) => {
    const k = ev.source && ev.source.kind ? ev.source.kind : 'manual';
    const who = ev.createdByName || '';
    return k === 'manual' ? (who ? t('city_events_by', { name: who, defaultValue: 'by {{name}}' }) : 'manual') : k + (ev.source && ev.source.feed ? ' · ' + ev.source.feed : '');
  };

  const openNew = () => setDialog({ form: emptyEvent(), previous: null });
  const openEdit = (ev) => setDialog({ form: clone(ev), previous: ev });
  const openDuplicate = (ev) => {
    const f = clone(ev);
    delete f.id; delete f.createdAt; delete f.createdBy; delete f.createdByName; delete f.submittedAt;
    delete f.approvedAt; delete f.approvedBy; delete f.approvedByName; delete f.rejectedReason; delete f.cancelledAt;
    f.status = STATUS.DRAFT; f.image = null; f.source = { kind: 'manual', ref: '', feed: '' };
    setDialog({ form: f, previous: null });
  };

  // Status transitions outside the dialog (supervisor row actions): a PATCH on
  // the stored node; saveCityEvent refuses it if the event changed since this
  // list was loaded, in which case the list is refreshed.
  const transition = async (ev, status, extra) => {
    setBusyId(ev.id);
    const patch = Object.assign({ status, updatedBy: uid, updatedByName: myName }, extra || {});
    const res = await dispatch(api.saveCityEvent({ id: ev.id }, { previous: ev, patch }));
    setBusyId(null);
    if (!res.success) refresh();
    setSnack(res.success
      ? { severity: 'success', msg: status === STATUS.PUBLISHED ? t('city_events_published', { defaultValue: 'Published.' }) : t('city_events_saved', { defaultValue: 'Saved.' }) }
      : { severity: 'error', msg: t('city_events_error', { msg: res.error, defaultValue: 'Could not save: {{msg}}' }) });
  };
  const approve = (ev) => transition(ev, STATUS.PUBLISHED, { approvedBy: uid, approvedByName: myName, approvedAt: Date.now(), rejectedReason: null });
  const unpublish = (ev) => transition(ev, STATUS.DRAFT, { approvedBy: null, approvedByName: null, approvedAt: null });
  const cancelEvent = async (ev) => {
    const ok = await confirmDialog({
      title: t('city_events_confirm_cancel', { defaultValue: 'Cancel this published event? Drivers stop seeing it within 10 minutes.' }),
      confirmLabel: t('city_events_btn_cancel_event', { defaultValue: 'Cancel event' }),
      cancelLabel: t('city_events_keep_event', { defaultValue: 'Keep event' }),
      destructive: true,
    });
    if (ok) transition(ev, STATUS.CANCELLED, { cancelledAt: Date.now() });
  };
  const doReject = async () => { const r = reject; setReject(null); await transition(r.ev, STATUS.REJECTED, { rejectedReason: r.reason || '' }); };
  const removeDraft = async (ev) => {
    const ok = await confirmDialog({
      title: t('city_events_confirm_delete', { defaultValue: 'Delete this draft?' }),
      confirmLabel: t('city_events_btn_delete', { defaultValue: 'Delete draft' }),
      destructive: true,
    });
    if (!ok) return;
    const res = await dispatch(api.deleteCityEvent(ev));
    setSnack(res.success ? { severity: 'success', msg: t('city_events_saved', { defaultValue: 'Saved.' }) } : { severity: 'error', msg: t('city_events_error', { msg: res.error, defaultValue: 'Could not save: {{msg}}' }) });
  };
  const canEditRow = (ev) => canApprove || (ev.createdBy === uid && ev.status !== STATUS.PUBLISHED && ev.status !== STATUS.CANCELLED);

  if (!isStaff) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography sx={{ color: c.INK_2 }}>{t('staff_access_required', { defaultValue: 'Staff access required.' })}</Typography>
      </Paper>
    );
  }

  const fetchedMin = cityeventsdata.fetchedAt ? Math.max(0, Math.round((Date.now() - cityeventsdata.fetchedAt) / 60000)) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: c.INK_2 }}>
            {t('city_events_subtitle', { defaultValue: 'Crowd events drivers will see in Hot zones — a venue pin, a demand window and a one-time push to nearby online drivers.' })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {fetchedMin !== null ? <Typography variant="caption" sx={{ color: c.TEXT_3 }}>{t('city_events_fetched', { n: fetchedMin, defaultValue: 'List loaded {{n}} min ago' })}</Typography> : null}
          <Button variant="outlined" color="neutral" startIcon={cityeventsdata.loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={refresh} disabled={cityeventsdata.loading}>
            {t('city_events_refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} disabled={!enabled && !canApprove}>
            {t('city_events_new', { defaultValue: 'New event' })}
          </Button>
        </Stack>
      </Stack>

      {!enabled ? (
        <InlineMessage kind="info" sx={{ mt: 2 }}>{t('city_events_off', { defaultValue: 'City events is switched off (Settings › General › City events).' })}</InlineMessage>
      ) : null}
      {cityeventsdata.error && cityeventsdata.error.flag ? <InlineMessage kind="error" sx={{ mt: 2 }}>{cityeventsdata.error.msg}</InlineMessage> : null}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mt: 2, borderBottom: `1px solid ${c.OUTLINE}` }}>
        <Tab value="upcoming" label={t('city_events_tab_upcoming', { defaultValue: 'Upcoming' })} />
        <Tab value="queue" label={<span>{t('city_events_tab_queue', { defaultValue: 'Review queue' })}{queueCount ? <StatusChip tone="warning" label={String(queueCount)} sx={{ ml: 1, height: 22 }} /> : null}</span>} />
        <Tab value="past" label={t('city_events_tab_past', { defaultValue: 'Recent past' })} />
        {canApprove ? <Tab value="impact" label={t('city_events_tab_impact', { defaultValue: 'Impact' })} /> : null}
        {isAdmin ? <Tab value="auto" label={t('city_events_tab_auto', { defaultValue: 'Auto events' })} /> : null}
      </Tabs>

      {tab === 'auto' && isAdmin ? (
        <AutoEventsPanel t={t} dispatch={dispatch} sources={cityeventsdata.sources || {}} events={list} onEdit={openEdit} onEventsChanged={refresh} setSnack={setSnack} />
      ) : null}
      {tab === 'impact' && canApprove ? (
        <ImpactPanel t={t} events={list} onRefresh={refresh} setSnack={setSnack} />
      ) : null}
      <Paper sx={{ mt: 2, overflowX: 'auto', display: tab === 'auto' || tab === 'impact' ? 'none' : 'block' }}>
        {cityeventsdata.loading && !list.length ? <Box p={3} textAlign="center"><CircularProgress size={28} /></Box> : null}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('city_events_col_when', { defaultValue: 'When (Addis)' })}</TableCell>
              <TableCell>{t('city_events_col_event', { defaultValue: 'Event' })}</TableCell>
              <TableCell>{t('city_events_col_venue', { defaultValue: 'Venue' })}</TableCell>
              <TableCell>{t('city_events_col_crowd', { defaultValue: 'Crowd' })}</TableCell>
              <TableCell>{t('city_events_col_source', { defaultValue: 'Source' })}</TableCell>
              <TableCell>{t('city_events_col_status', { defaultValue: 'Status' })}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {tab === 'queue' ? t('city_events_queue_empty', { defaultValue: 'Nothing waiting for approval.' }) : t('city_events_empty', { defaultValue: 'No events in this view.' })}
              </Typography></TableCell></TableRow>
            ) : rows.slice(safePage * ROWS_PER_PAGE, (safePage + 1) * ROWS_PER_PAGE).map((ev) => (
              <TableRow key={ev.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtWhen(ev)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.title && ev.title.en}</Typography>
                  <Typography variant="caption" color="text.secondary">{categoryLabel(ev.category)}{ev.title && ev.title.am ? ' · ' + ev.title.am : ''}</Typography>
                  {ev.status === STATUS.REJECTED && ev.rejectedReason ? <Typography variant="caption" color="error" display="block">{ev.rejectedReason}</Typography> : null}
                </TableCell>
                <TableCell>{ev.venue && ev.venue.name}</TableCell>
                <TableCell>{sizeLabel(ev.size)}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={sourceLabel(ev)} /></TableCell>
                <TableCell>
                  {statusChip(ev.status, statusLabel(ev.status))}
                  {ev.push && Number(ev.push.sent) > 0 ? (
                    <Typography variant="caption" color="text.secondary" display="block">{t('city_events_push_sent', { defaultValue: 'Push sent to {{n}} drivers', n: Number(ev.push.sent) })}</Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {busyId === ev.id ? <CircularProgress size={18} /> : (
                    <>
                      {canApprove && ev.status === STATUS.PENDING ? (
                        <>
                          <Tooltip title={t('city_events_btn_approve', { defaultValue: 'Approve & publish' })}><IconButton size="small" color="success" onClick={() => approve(ev)}><CheckIcon /></IconButton></Tooltip>
                          <Tooltip title={t('city_events_btn_reject', { defaultValue: 'Reject' })}><IconButton size="small" color="error" onClick={() => setReject({ ev, reason: '' })}><BlockIcon /></IconButton></Tooltip>
                        </>
                      ) : null}
                      {canApprove && ev.status === STATUS.PUBLISHED ? (
                        <>
                          <Tooltip title={t('city_events_btn_unpublish', { defaultValue: 'Unpublish' })}><IconButton size="small" onClick={() => unpublish(ev)}><UndoIcon /></IconButton></Tooltip>
                          <Tooltip title={t('city_events_btn_cancel_event', { defaultValue: 'Cancel event' })}><IconButton size="small" color="error" onClick={() => cancelEvent(ev)}><CancelIcon /></IconButton></Tooltip>
                        </>
                      ) : null}
                      {canEditRow(ev) ? <Tooltip title={t('city_events_btn_edit', { defaultValue: 'Edit' })}><IconButton size="small" onClick={() => openEdit(ev)}><EditIcon /></IconButton></Tooltip> : null}
                      <Tooltip title={t('city_events_btn_duplicate', { defaultValue: 'Duplicate' })}><IconButton size="small" onClick={() => openDuplicate(ev)}><ContentCopyIcon /></IconButton></Tooltip>
                      {canEditRow(ev) && (ev.status === STATUS.DRAFT || ev.status === STATUS.REJECTED) ? (
                        <Tooltip title={t('city_events_btn_delete', { defaultValue: 'Delete draft' })}><IconButton size="small" onClick={() => removeDraft(ev)}><DeleteOutlineIcon /></IconButton></Tooltip>
                      ) : null}
                      {!canApprove && ev.status === STATUS.PENDING ? <Typography variant="caption" color="text.secondary">{t('city_events_waiting', { defaultValue: 'Waiting for a supervisor' })}</Typography> : null}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={rows.length} page={safePage} onPageChange={(e, p) => setPage(p)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]} />
      </Paper>

      {dialog ? (
        <CityEventDialog
          t={t}
          form={dialog.form}
          previous={dialog.previous}
          others={list}
          canApprove={canApprove}
          uid={uid}
          myName={myName}
          onClose={() => setDialog(null)}
          onSaved={(res, status) => {
            setDialog(null);
            setSnack(res.success
              ? { severity: 'success', msg: status === STATUS.PENDING ? t('city_events_submitted', { defaultValue: 'Submitted — a supervisor will review it.' }) : status === STATUS.PUBLISHED ? t('city_events_published', { defaultValue: 'Published.' }) : t('city_events_saved', { defaultValue: 'Saved.' }) }
              : { severity: 'error', msg: t('city_events_error', { msg: res.error, defaultValue: 'Could not save: {{msg}}' }) });
          }}
        />
      ) : null}

      <Dialog open={Boolean(reject)} onClose={() => setReject(null)} maxWidth="xs" fullWidth fullScreen={phone}>
        <DialogTitle>{t('city_events_btn_reject', { defaultValue: 'Reject' })}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={2} margin="dense" label={t('city_events_reject_reason', { defaultValue: 'Reason for the agent (optional)' })} value={reject ? reject.reason : ''} onChange={(e) => setReject(Object.assign({}, reject, { reason: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReject(null)}>{t('city_events_btn_cancel', { defaultValue: 'Cancel' })}</Button>
          <Button color="error" variant="contained" onClick={doReject}>{t('city_events_btn_reject', { defaultValue: 'Reject' })}</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The rider app shows sourceUrl as “More info” only when it is a whole http(s)
// link under 300 chars (functions/common/cityEventsRider.js safeUrl): a bare
// “www.example.com” gets https:// on save, anything else is flagged.
const RIDER_LINK_MAX = 300;
const normalizeRiderLink = (v) => {
  const s = String(v || '').trim();
  if (!s || /^https?:\/\//i.test(s)) return s;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#:]|$)/i.test(s) ? 'https://' + s : s;
};
const riderLinkOk = (v) => {
  const s = normalizeRiderLink(v);
  return !s || (/^https?:\/\/[^/\s]\S*$/i.test(s) && s.length < RIDER_LINK_MAX);
};

function CityEventDialog({ t, form: initial, previous, others, canApprove, uid, myName, onClose, onSaved }) {
  const phone = usePhone();
  const dispatch = useDispatch();
  const mapsReady = useMapsReady();
  const [form, setForm] = useState(initial);
  const [windowsTouched, setWindowsTouched] = useState(Boolean(initial.id));
  const [posterBlob, setPosterBlob] = useState(null);
  const [posterPreview, setPosterPreview] = useState(initial.image ? initial.image.url : null);
  const [posterBusy, setPosterBusy] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  // Import from an Eventbrite link (eventbriteLookup): fills the form; the
  // agent still checks it and saves through the normal approval flow.
  const { auth: firebaseAuth, config } = useContext(FirebaseContext);
  const [ebLink, setEbLink] = useState('');
  const [ebBusy, setEbBusy] = useState(false);
  const [ebNote, setEbNote] = useState(null);
  const [ebError, setEbError] = useState(null);
  // What the last import wrote, so a second import replaces it (and never what the agent typed).
  const imported = useRef({ notes: null, poster: null });
  const windowsTouchedRef = useRef(Boolean(initial.id));
  const posterPreviewRef = useRef(initial.image ? initial.image.url : null);
  // Place search for the exact location: the same search as the booking
  // screen (bundled Addis landmarks first, Google places only when needed).
  const [placeSearch, setPlaceSearch] = useState(null);

  const set = (patch) => setForm((f) => Object.assign({}, f, patch));
  const setVenue = (patch) => setForm((f) => Object.assign({}, f, { venue: Object.assign({}, f.venue, patch) }));
  const setWindow = (kind, field, value) => {
    setWindowsTouched(true);
    windowsTouchedRef.current = true;
    setForm((f) => { const w = clone(f.windows || WINDOW_DEFAULTS.other); w[kind][field] = value; return Object.assign({}, f, { windows: w }); });
  };
  const onCategory = (c) => setForm((f) => Object.assign({}, f, { category: c }, windowsTouched ? {} : { windows: clone(WINDOW_DEFAULTS[c] || WINDOW_DEFAULTS.other) }));

  const place = PLACES.find((p) => p.key === form.venue.placeKey) || null;
  // null / '' is "no coordinate" (Number(null) is 0 — the Gulf of Guinea).
  const venueLat = form.venue.lat; const venueLng = form.venue.lng;
  const pin = useMemo(
    () => (hasCoord(venueLat) && hasCoord(venueLng) ? { lat: Number(venueLat), lng: Number(venueLng) } : null),
    [venueLat, venueLng]
  );
  const dropPin = (lat, lng) => {
    const near = nearestPlace(lat, lng);
    setVenue(Object.assign({ lat, lng, placeKey: null }, !form.venue.name && near ? { name: near.en, nameAm: near.am || '' } : {}));
  };
  // A picked search result sets the venue name and the exact pin; the map
  // recentres on it. Hot zones only covers Addis Ababa, so a place more than
  // 40 km away is refused. The Amharic name is kept only for the same place.
  const onPlaceSearch = (value) => {
    setPlaceSearch(value || null);
    if (!value || !value.coords) return;
    const lat = Number(value.coords.lat);
    const lng = Number(value.coords.lng);
    if (!hasCoord(lat) || !hasCoord(lng)) return;
    if (haversineKm(ADDIS_CENTER.lat, ADDIS_CENTER.lng, lat, lng) > 40) {
      setError(t('city_events_place_outside', { defaultValue: 'That place is outside Addis Ababa. Pick a place in the city.' }));
      return;
    }
    setError(null);
    const name = String(value.description || '').split(',')[0].trim();
    const same = name ? PLACES.find((pl) => pl.en && pl.en.toLowerCase() === name.toLowerCase()) : null;
    setVenue({ name: name || form.venue.name || '', nameAm: same ? (same.am || '') : '', lat, lng, placeKey: same ? same.key : null });
  };
  const onPlace = (e, v) => {
    if (v && typeof v === 'object') setVenue({ placeKey: v.key, name: v.en, nameAm: v.am || '', lat: v.lat, lng: v.lng });
    else setVenue({ placeKey: null, name: typeof v === 'string' ? v : '' });
  };

  const onPoster = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setPosterBusy(true);
    try {
      const blob = await compressImage(file);
      setPosterBlob(blob);
      setPosterPreview(URL.createObjectURL(blob));
    } catch (err) { setError(String(err.message || err)); }
    setPosterBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const clearPoster = () => { setPosterBlob(null); setPosterPreview(null); set({ image: null }); };
  useEffect(() => { posterPreviewRef.current = posterPreview; }, [posterPreview]);

  const importEventbrite = async () => {
    const link = ebLink.trim();
    if (!link || ebBusy) return;
    setEbBusy(true); setEbNote(null); setEbError(null);
    try {
      const projectId = (config && config.projectId) || 'donkey-drive-5cdd1';
      let res;
      try {
        res = await callDirectFunction(firebaseAuth, projectId, directFunctionRegion(config), 'eventbriteLookup', { url: link });
      } catch (err) {
        const byCode = {
          no_key: t('city_events_eb_err_no_key', { defaultValue: 'Eventbrite is not connected yet. An admin adds the key in Auto events › Eventbrite.' }),
          bad_link: t('city_events_eb_err_bad_link', { defaultValue: 'That is not an Eventbrite event link.' }),
          not_found: t('city_events_eb_err_not_found', { defaultValue: 'Eventbrite has no such event, or it is private.' }),
          auth: t('city_events_eb_err_key', { defaultValue: 'Eventbrite rejected the key. An admin can check it in Auto events › Eventbrite.' }),
          forbidden: t('city_events_eb_err_key', { defaultValue: 'Eventbrite rejected the key. An admin can check it in Auto events › Eventbrite.' }),
          parse: t('city_events_eb_no_date', { defaultValue: 'Eventbrite gives no date for this event.' }),
          network: t('city_events_eb_err_unreachable', { defaultValue: 'Eventbrite could not be reached. Try again in a moment.' }),
          http: t('city_events_eb_err_unreachable', { defaultValue: 'Eventbrite could not be reached. Try again in a moment.' }),
        };
        throw new Error(byCode[err.code] || err.message);
      }
      const e = res.event || {};
      if (e.status && e.status !== 'live' && e.status !== 'started') throw new Error(t('city_events_eb_not_live', { defaultValue: 'This event is cancelled or not published on Eventbrite.' }));
      if (e.online) throw new Error(t('city_events_eb_online', { defaultValue: 'This is an online event, so there is no venue for drivers.' }));
      if (!Number.isFinite(Number(e.startMs))) throw new Error(t('city_events_eb_no_date', { defaultValue: 'Eventbrite gives no date for this event.' }));
      const start = Number(e.startMs);
      const end = Number(e.endMs) > start ? Number(e.endMs) : start + 3 * 3600e3;
      if (end < Date.now()) throw new Error(t('city_events_eb_ended', { defaultValue: 'This event has already ended.' }));
      const v = e.venue;
      let venuePatch = null;
      let venueNote = null;
      if (v && v.inAddis && hasCoord(v.lat) && hasCoord(v.lng)) {
        // Amharic name only for the same place: the server's same-place match,
        // a known place of exactly that name, or — when Eventbrite gives no
        // name at all — the nearest place used as the name too.
        const near = nearestPlace(Number(v.lat), Number(v.lng));
        const named = v.name ? PLACES.find((p) => p.en && p.en.toLowerCase() === String(v.name).trim().toLowerCase()) : null;
        const label = named || (!v.name ? near : null);
        venuePatch = { name: v.name || (near ? near.en : ''), nameAm: v.nameAm || (label ? (label.am || '') : ''), lat: Number(v.lat), lng: Number(v.lng), placeKey: null };
      } else if (v && hasCoord(v.lat) && hasCoord(v.lng)) {
        venueNote = t('city_events_eb_outside', { defaultValue: 'The venue is outside Addis Ababa. Pick a venue in the city or drop a pin.' });
      } else {
        venueNote = t('city_events_eb_no_venue', { defaultValue: 'Eventbrite gives no venue for this event. Pick a venue or drop a pin.' });
      }
      const summary = String(e.summary || '');
      // React runs the updater later: capture the previous import's notes now.
      const prevImportedNotes = imported.current.notes;
      // Applied to the LATEST form, so anything typed while the lookup ran is kept.
      setForm((f) => {
        const next = Object.assign({}, f, {
          start, end, sourceUrl: e.url || link,
          // The nightly Eventbrite feed skips an event whose id is already here.
          source: Object.assign({ kind: 'manual', feed: '' }, f.source || {}, { ref: e.id ? 'eventbrite:' + e.id : ((f.source && f.source.ref) || '') }),
        });
        if (e.title) next.title = Object.assign({}, f.title, { en: e.title });
        const notesNow = String(f.notes || '');
        if (!notesNow.trim() || notesNow === prevImportedNotes) next.notes = summary;
        if (CATEGORIES.indexOf(e.category) >= 0) {
          next.category = e.category;
          if (!windowsTouchedRef.current) next.windows = clone(WINDOW_DEFAULTS[e.category] || WINDOW_DEFAULTS.other);
        }
        if ([1, 2, 3].indexOf(e.size) >= 0) next.size = e.size;
        if (venuePatch) next.venue = Object.assign({}, f.venue, venuePatch);
        return next;
      });
      imported.current.notes = summary;   // what this import offered: a later import may replace exactly this
      // Poster: replace only an empty one or the previous import's (read at
      // answer time, so a poster picked during the lookup is kept).
      const posterNow = posterPreviewRef.current;
      if (!posterNow || posterNow === imported.current.poster) {
        let blob = null;
        let url = null;
        if (e.imageDataUrl) {
          try {
            blob = await compressImage(await (await fetch(e.imageDataUrl)).blob());
            url = URL.createObjectURL(blob);
          } catch (_) { blob = null; url = null; }
        }
        setPosterBlob(blob);
        setPosterPreview(url);
        imported.current.poster = url;
      }
      setEbNote(venueNote || t('city_events_eb_filled', { defaultValue: 'Filled in from Eventbrite. Check the venue, times and crowd size before saving.' }));
    } catch (err) {
      setEbError(String(err.message || err));
    } finally {
      setEbBusy(false);
    }
  };

  // Duplicate hint: same venue cell + same Addis hour + similar title.
  const dup = useMemo(() => {
    if (!pin) return null;
    const cell = cellOf(pin.lat, pin.lng);
    const key = api.cityEventDedupeKey(Object.assign({}, form, { venue: Object.assign({}, form.venue, { cell: cell ? cell.key : null }) }));
    return (others || []).find((o) => o.id !== form.id && o.status !== STATUS.CANCELLED && o.status !== STATUS.REJECTED && o.dedupeKey === key) || null;
  }, [others, form, pin]);

  const validate = () => {
    if (!form.title || !String(form.title.en || '').trim()) return t('city_events_err_title', { defaultValue: 'Enter an English title.' });
    if (!pin) return t('city_events_err_venue', { defaultValue: 'Pick a venue or drop a pin on the map.' });
    if (!(Number(form.end) > Number(form.start))) return t('city_events_err_time', { defaultValue: 'The end must be after the start.' });
    if (Number(form.end) < Date.now() - 3600e3 && !form.id) return t('city_events_err_past', { defaultValue: 'The event has already ended.' });
    return null;
  };

  const save = async (status) => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null); setSaving(status);
    const now = Date.now();
    const ev = Object.assign({}, form, {
      sourceUrl: normalizeRiderLink(form.sourceUrl),
      title: { en: String(form.title.en || '').trim(), am: String(form.title.am || '').trim() },
      venue: Object.assign({}, form.venue, { name: String(form.venue.name || '').trim() || (place ? place.en : '') }),
      status,
      updatedBy: uid, updatedByName: myName,
    });
    if (!ev.id) { ev.createdBy = uid; ev.createdByName = myName; }
    if (status === STATUS.PENDING) ev.submittedAt = now;
    // Approval stamp only on the transition INTO published; a later edit of a
    // published event keeps the original approver (updatedBy records the editor).
    if (status === STATUS.PUBLISHED && !(previous && previous.status === STATUS.PUBLISHED)) {
      ev.approvedBy = uid; ev.approvedByName = myName; ev.approvedAt = now; ev.rejectedReason = null;
    }
    const res = await dispatch(api.saveCityEvent(ev, { previous, posterBlob }));
    setSaving(null);
    if (!res.success) { setError(res.error); return; }
    onSaved(res, status);
  };

  const w = form.windows || WINDOW_DEFAULTS.other;
  const numField = (label, value, onChange, props) => (
    <TextField size="small" type="number" label={label} value={value} onChange={(e) => onChange(e.target.value)} inputProps={{ step: props && props.step ? props.step : 5, min: 0 }} fullWidth />
  );
  // before / after / weight in three equal columns, so both windows line up.
  const windowRowSx = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 };
  const isPublished = form.status === STATUS.PUBLISHED;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={phone}>
      <DialogTitle sx={{ pb: 0.5 }}>
        {form.id ? t('city_events_dialog_edit', { defaultValue: 'Edit city event' }) : t('city_events_dialog_new', { defaultValue: 'New city event' })}
        <Typography variant="body2" color="text.secondary">{t('city_events_dialog_hint', { defaultValue: 'Drivers see it in Hot zones once a supervisor approves it.' })}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {!form.id || form.status === STATUS.DRAFT ? (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small" sx={{ flex: '1 1 280px' }}
              label={t('city_events_eb_link', { defaultValue: 'Import from Eventbrite: paste the event link' })}
              value={ebLink} onChange={(e) => setEbLink(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); importEventbrite(); } }}
              InputProps={{ startAdornment: <LinkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <Button variant="outlined" color="neutral" onClick={importEventbrite} disabled={ebBusy || !ebLink.trim()} sx={{ height: 40 }}>
              {ebBusy ? <CircularProgress size={16} /> : t('city_events_eb_import', { defaultValue: 'Import' })}
            </Button>
          </Box>
        ) : null}
        {ebNote ? <InlineMessage kind="info" sx={{ mb: 2 }} onClose={() => setEbNote(null)}>{ebNote}</InlineMessage> : null}
        {ebError ? <InlineMessage kind="error" sx={{ mb: 2 }} onClose={() => setEbError(null)}>{ebError}</InlineMessage> : null}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, pt: 1 }}>
          <Box sx={{ minWidth: 0 }}><TextField fullWidth autoFocus label={t('city_events_f_title_en', { defaultValue: 'Title (English)' })} value={form.title.en || ''} onChange={(e) => set({ title: Object.assign({}, form.title, { en: e.target.value }) })} /></Box>
          <Box sx={{ minWidth: 0 }}><TextField fullWidth label={t('city_events_f_title_am', { defaultValue: 'Title (Amharic)' })} value={form.title.am || ''} onChange={(e) => set({ title: Object.assign({}, form.title, { am: e.target.value }) })} /></Box>
          <Box sx={{ minWidth: 0 }}>
            <TextField select fullWidth label={t('city_events_f_category', { defaultValue: 'Category' })} value={form.category} onChange={(e) => onCategory(e.target.value)}>
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{t('city_events_cat_' + c, c.charAt(0).toUpperCase() + c.slice(1))}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Autocomplete
              freeSolo
              options={PLACES}
              value={place || form.venue.name || ''}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.en + (o.am ? ' · ' + o.am : ''))}
              onChange={onPlace}
              onInputChange={(e, v, reason) => { if (reason === 'input') setVenue({ name: v, placeKey: null }); }}
              renderInput={(params) => <TextField {...params} label={t('city_events_f_venue', { defaultValue: 'Venue (pick a place or drop a pin)' })} />}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <AddisTimeField id="ce-start" label={t('city_events_f_start', { defaultValue: 'Starts (Addis time)' })} value={form.start}
              onChange={(ms) => set({ start: ms, end: Number(form.end) > ms ? form.end : ms + 3 * 3600e3 })} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <AddisTimeField id="ce-end" label={t('city_events_f_end', { defaultValue: 'Ends (Addis time)' })} value={form.end} onChange={(ms) => set({ end: ms })} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>{fmtWhen(form)}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{t('city_events_f_size', { defaultValue: 'Expected crowd' })}</Typography>
            <Box>
              <ToggleButtonGroup exclusive size="small" value={form.size} onChange={(e, v) => { if (v) set({ size: v }); }} sx={{ mt: 0.5 }}>
                {[1, 2, 3].map((n) => <ToggleButton key={n} value={n}>{t('city_events_size_' + n, { 1: 'Hundreds', 2: 'Thousands', 3: 'Tens of thousands' }[n])}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{t('city_events_f_windows', { defaultValue: 'Demand windows (pre-filled by category, editable)' })}</Typography>
            <Typography variant="body2" sx={{ mt: 0.75, mb: 1.5 }}>{t('city_events_w_arrival', { defaultValue: 'Arrival (before → after start, min)' })}</Typography>
            <Box sx={windowRowSx}>
              {numField('before', w.arrival.before, (v) => setWindow('arrival', 'before', num(v, 0)))}
              {numField('after', w.arrival.after, (v) => setWindow('arrival', 'after', num(v, 0)))}
              {numField(t('city_events_w_weight', { defaultValue: 'weight' }), w.arrival.weight, (v) => setWindow('arrival', 'weight', Math.min(1, Math.max(0, num(v, 0)))), { step: 0.1 })}
            </Box>
            <Typography variant="body2" sx={{ mt: 2, mb: 1.5 }}>{t('city_events_w_exit', { defaultValue: 'Exit wave (before → after end, min)' })}</Typography>
            <Box sx={windowRowSx}>
              {numField('before', w.exit.before, (v) => setWindow('exit', 'before', num(v, 0)))}
              {numField('after', w.exit.after, (v) => setWindow('exit', 'after', num(v, 0)))}
              {numField(t('city_events_w_weight', { defaultValue: 'weight' }), w.exit.weight, (v) => setWindow('exit', 'weight', Math.min(1, Math.max(0, num(v, 0)))), { step: 0.1 })}
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{t('city_events_f_pin', { defaultValue: 'Pin (click the map or drag the marker)' })}</Typography>
            <Box sx={{ mt: 1, mb: 1 }}>
              <GoogleMapsAutoComplete
                variant="outlined"
                placeholder={t('city_events_f_place_search', { defaultValue: 'Search any place for its exact location' })}
                value={placeSearch}
                onChange={onPlaceSearch}
              />
            </Box>
            <Box sx={{ border: `1px solid ${c.OUTLINE_STRONG}`, borderRadius: '12px', overflow: 'hidden', height: 220, position: 'relative' }}>
              {mapsReady ? (
                <GoogleMap
                  mapContainerStyle={{ height: 220, width: '100%' }}
                  center={pin || ADDIS_CENTER}
                  zoom={pin ? 15 : 12}
                  options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, clickableIcons: false }}
                  onClick={(e) => dropPin(e.latLng.lat(), e.latLng.lng())}
                >
                  {pin ? <Marker position={pin} draggable onDragEnd={(e) => dropPin(e.latLng.lat(), e.latLng.lng())} /> : null}
                </GoogleMap>
              ) : <Box p={2}><CircularProgress size={20} /></Box>}
              {pin ? (
                <Typography variant="caption" sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: 'rgba(255,255,255,.92)', px: 1, borderRadius: 1 }}>
                  {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}{(() => { const c = cellOf(pin.lat, pin.lng); return c ? ' · ' + c.key : ''; })()}
                </Typography>
              ) : null}
            </Box>
            <TextField fullWidth size="small" sx={{ mt: 2 }} label={t('city_events_f_venue_am', { defaultValue: 'Venue name (Amharic)' })} value={form.venue.nameAm || ''} onChange={(e) => setVenue({ nameAm: e.target.value })} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{t('city_events_f_poster', { defaultValue: 'Poster (optional, compressed to 300 KB)' })}</Typography>
            <Box sx={{ mt: 0.5, border: `1px dashed ${c.OUTLINE_STRONG}`, borderRadius: '12px', p: 1, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 72 }}>
              {posterPreview ? <Box component="img" src={posterPreview} alt="" sx={{ width: 72, height: 54, objectFit: 'cover', borderRadius: '6px' }} /> : <ImageIcon sx={{ color: c.TEXT_3 }} />}
              <Box sx={{ flex: 1 }}>
                <Button size="small" component="label" disabled={posterBusy}>
                  {posterBusy ? <CircularProgress size={16} /> : (posterPreview ? t('city_events_btn_edit', { defaultValue: 'Edit' }) : t('city_events_f_poster', { defaultValue: 'Poster' }))}
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPoster} />
                </Button>
                {posterPreview ? <Button size="small" color="inherit" onClick={clearPoster}>{t('city_events_btn_cancel', { defaultValue: 'Cancel' })}</Button> : null}
                {posterBlob ? <Typography variant="caption" display="block" color="text.secondary">{Math.round(posterBlob.size / 1024)} KB</Typography> : null}
              </Box>
            </Box>
            <TextField
              fullWidth size="small" sx={{ mt: 2 }}
              label={t('city_events_f_source', { defaultValue: 'Source link' })}
              error={!riderLinkOk(form.sourceUrl)}
              helperText={riderLinkOk(form.sourceUrl)
                ? t('city_events_f_source_hint', { defaultValue: 'Public page only — customers see it as “More info” when City events for customers is on.' })
                : t('city_events_f_source_bad', { defaultValue: 'Customers won’t see this link: it must start with https:// (or http://), contain no spaces and be shorter than 300 characters.' })}
              value={form.sourceUrl || ''}
              onChange={(e) => set({ sourceUrl: e.target.value })}
            />
            <TextField fullWidth size="small" sx={{ mt: 2 }} multiline minRows={2} label={t('city_events_f_notes', { defaultValue: 'Notes for drivers (optional)' })} value={form.notes || ''} onChange={(e) => set({ notes: e.target.value })} />
          </Box>
        </Box>
        {dup ? <InlineMessage kind="warning" sx={{ mt: 2 }}>{t('city_events_dup', { title: dup.title && dup.title.en, by: dup.createdByName || dup.source?.kind || '', defaultValue: 'Possible duplicate of “{{title}}” ({{by}}) — same place and hour.' })}</InlineMessage> : null}
        {error ? <InlineMessage kind="error" sx={{ mt: 2 }}>{error}</InlineMessage> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {form.id ? statusLine(t, form) : null}
          </Typography>
        </Box>
        <Button onClick={onClose} disabled={Boolean(saving)}>{t('city_events_btn_cancel', { defaultValue: 'Cancel' })}</Button>
        {!isPublished ? (
          <Button variant="outlined" onClick={() => save(STATUS.DRAFT)} disabled={Boolean(saving)}>{saving === STATUS.DRAFT ? <CircularProgress size={18} /> : t('city_events_btn_draft', { defaultValue: 'Save draft' })}</Button>
        ) : null}
        {!isPublished && !canApprove ? (
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => save(STATUS.PENDING)} disabled={Boolean(saving)}>
            {saving === STATUS.PENDING ? <CircularProgress size={18} color="inherit" /> : t('city_events_btn_submit', { defaultValue: 'Submit for approval' })}
          </Button>
        ) : null}
        {canApprove ? (
          <Button variant="contained" startIcon={<CheckIcon />} onClick={() => save(STATUS.PUBLISHED)} disabled={Boolean(saving)}>
            {saving === STATUS.PUBLISHED ? <CircularProgress size={18} color="inherit" /> : (isPublished ? t('city_events_saved_publish', { defaultValue: 'Save' }) : t('city_events_btn_approve', { defaultValue: 'Approve & publish' }))}
          </Button>
        ) : null}
      </DialogActions>
      <Divider />
    </Dialog>
  );
}

// ── Auto events (phase 4, admin only) ─────────────────────────────────────────
// The connector registry the tab lists (mirrors functions/common/cityEventsAuto.js
// CONNECTORS); the stored row in /city_events_sources carries enabled / lastRun /
// lastResult and the ICS feeds the admin adds.
const CONNECTORS = [
  { id: 'calendar', name: 'Built-in Ethiopian calendar', price: 'free', builtin: true, desc: 'Orthodox movable feasts, Timket / Meskel / Enkutatash, Genna, national days; Islamic feasts (±1 day, moon sighting) go to the review queue.' },
  { id: 'weekly', name: 'Weekly crowd patterns', price: 'free', builtin: true, desc: "Jumu'ah at Grand Anwar, Sunday services at Bole Medhanialem / Holy Trinity / St. George, Merkato Saturday — next 7 days." },
  { id: 'eventbrite', name: 'Eventbrite', price: 'free', keyed: true, requires: ['token'], desc: 'Paste an Eventbrite link in the New event form to fill it in; nightly, the upcoming events of the Eventbrite organisations you tick go to the review queue.' },
  { id: 'predicthq', name: 'PredictHQ', price: 'paid', designed: true, requires: ['token'], desc: 'Concerts, sports, conferences with attendance estimates.' },
  { id: 'apifootball', name: 'API-Football', price: 'paid', designed: true, requires: ['key'], desc: 'Ethiopian Premier League fixtures → stadium match days.' },
  { id: 'aerodatabox', name: 'AeroDataBox', price: 'paid', designed: true, requires: ['key'], desc: 'Bole airport arrival waves → airport demand windows.' },
];
// Direct Cloud Functions URL (https://<region>-<projectId>.cloudfunctions.net/<name>)
// with the admin's ID token — same pattern as the Security Dashboard tools.
const directFunctionRegion = (config) => {
  const parts = String((config && config.databaseURL) || '').replace(/^https?:\/\//, '').split('/')[0].split('.');
  return parts.length === 4 ? parts[1] : 'us-central1';
};
const callDirectFunction = async (firebaseAuth, projectId, region, name, params) => {
  const currentUser = firebaseAuth && firebaseAuth.currentUser;
  if (!currentUser) throw new Error('Not authenticated');
  const idToken = await currentUser.getIdToken();
  const qs = new URLSearchParams();
  Object.keys(params || {}).forEach((k) => { const v = params[k]; if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
  const query = qs.toString();
  const url = `https://${region}-${projectId}.cloudfunctions.net/${name}${query ? `?${query}` : ''}`;
  let response;
  try {
    response = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } });
  } catch (err) {
    throw new Error(`${name}: ${err.message} (network or CORS — the portal origin must be allowlisted in the function)`);
  }
  const text = await response.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) { body = null; }
  if (!response.ok) {
    const err = new Error((body && body.error) || (text ? text.slice(0, 200) : '') || response.statusText || 'request failed');
    err.code = (body && body.code) || null;
    throw err;
  }
  if (!body || typeof body !== 'object') throw new Error(`${name}: empty or non-JSON response`);
  return body;
};

// POST variant for the Eventbrite key: a body never appears in a URL (URLs
// end up in request logs), so the token only ever travels this way.
const postDirectFunction = async (firebaseAuth, projectId, region, name, body) => {
  const currentUser = firebaseAuth && firebaseAuth.currentUser;
  if (!currentUser) throw new Error('Not authenticated');
  const idToken = await currentUser.getIdToken();
  const url = `https://${region}-${projectId}.cloudfunctions.net/${name}`;
  let response;
  try {
    response = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  } catch (err) {
    throw new Error(`${name}: ${err.message} (network or CORS — the portal origin must be allowlisted in the function)`);
  }
  const text = await response.text();
  let out = null;
  try { out = text ? JSON.parse(text) : null; } catch (_) { out = null; }
  if (!response.ok) {
    const err = new Error((out && out.error) || (text ? text.slice(0, 200) : '') || response.statusText || 'request failed');
    err.code = (out && out.code) || null;
    throw err;
  }
  if (!out || typeof out !== 'object') throw new Error(`${name}: empty or non-JSON response`);
  return out;
};

// Eventbrite key (admin): paste / replace / test / remove the private token
// and tick the organisations whose events the nightly run imports. The token
// is sent once to eventbriteKey, tested with Eventbrite and stored server-only;
// this dialog only ever sees its last 4 characters.
function EventbriteKeyDialog({ t, row, onClose, firebaseAuth, projectId, region, dispatch, setSnack }) {
  const phone = usePhone();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(null); // 'save' | 'test' | 'remove' | 'orgs'
  const [error, setError] = useState(null);
  const status = row || {};
  const available = status.organizationsAvailable && typeof status.organizationsAvailable === 'object' ? status.organizationsAvailable : {};
  const picked = status.organizations && typeof status.organizations === 'object' ? status.organizations : {};
  const call = async (action, extra) => {
    setBusy(action); setError(null);
    try {
      const answer = await postDirectFunction(firebaseAuth, projectId, region, 'eventbriteKey', Object.assign({ action }, extra || {}));
      await dispatch(api.fetchCityEventSources());
      return answer;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    const tok = token.trim();
    if (!tok || busy) return;
    if (await call('save', { token: tok })) {
      setToken('');
      setSnack({ severity: 'success', msg: t('city_events_eb_saved_ok', { defaultValue: 'Key saved and working.' }) });
    }
  };
  const test = async () => {
    const answer = await call('test');
    if (!answer) return;
    if (answer.keyError) setSnack({ severity: 'warning', msg: t('city_events_eb_key_error', { error: answer.keyError, defaultValue: 'Key problem: {{error}}' }) });
    else setSnack({ severity: 'success', msg: t('city_events_eb_tested', { defaultValue: 'Key checked with Eventbrite.' }) });
  };
  const remove = async () => {
    const ok = await confirmDialog({
      title: t('city_events_eb_confirm_remove', { defaultValue: 'Remove the Eventbrite key? Imports stop until a new key is added.' }),
      confirmLabel: t('city_events_eb_remove', { defaultValue: 'Remove key' }),
      destructive: true,
    });
    if (!ok) return;
    if (await call('remove')) setSnack({ severity: 'success', msg: t('city_events_eb_removed', { defaultValue: 'Key removed.' }) });
  };
  const toggleOrg = async (id, on) => {
    const next = {};
    // Only organisations the key still reaches can stay ticked.
    Object.keys(picked).forEach((k) => { if (picked[k] === true && k !== id && Object.prototype.hasOwnProperty.call(available, k)) next[k] = true; });
    if (on) next[id] = true;
    setBusy('orgs'); setError(null);
    const res = await dispatch(api.saveCityEventSource('eventbrite', { kind: 'eventbrite', name: 'Eventbrite', organizations: Object.keys(next).length ? next : null }));
    setBusy(null);
    if (!res.success) setError(res.error);
  };
  const setNightly = async (on) => {
    setBusy('orgs'); setError(null);
    const res = await dispatch(api.saveCityEventSource('eventbrite', { kind: 'eventbrite', name: 'Eventbrite', enabled: on }));
    setBusy(null);
    if (!res.success) setError(res.error);
  };
  const savedLine = status.keyLast4 ? t('city_events_eb_key_saved', {
    last4: status.keyLast4,
    account: (status.account && status.account.name) || '—',
    when: status.verifiedAt ? fmtDayTime(status.verifiedAt) : '—',
    defaultValue: 'Key ending {{last4}} · {{account}} · checked {{when}}',
  }) : null;

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={phone}>
      <DialogTitle>{t('city_events_eb_key_title', { defaultValue: 'Eventbrite key' })}</DialogTitle>
      <DialogContent dividers>
        {status.keyLast4 ? (
          <InlineMessage kind={status.keyError ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {savedLine}
            {status.keyError ? <Typography variant="caption" display="block">{t('city_events_eb_key_error', { error: status.keyError, defaultValue: 'Key problem: {{error}}' })}</Typography> : null}
          </InlineMessage>
        ) : (
          <InlineMessage kind="info" sx={{ mb: 2 }}>{t('city_events_eb_key_none', { defaultValue: 'No key yet.' })}</InlineMessage>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('city_events_eb_key_help', { defaultValue: 'Paste the private token from Eventbrite: Account settings › Developer links › API keys. It is tested with Eventbrite and stored on the server only; nobody can read it back, not even admins.' })}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small" type="password" autoComplete="off" sx={{ flex: '1 1 260px' }} disabled={Boolean(busy)}
            label={status.keyLast4 ? t('city_events_eb_token_new', { defaultValue: 'New private token' }) : t('city_events_eb_token', { defaultValue: 'Private token' })}
            value={token} onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
            inputProps={{ spellCheck: false, autoCapitalize: 'off', 'data-lpignore': 'true' }}
          />
          <Button variant="contained" onClick={save} disabled={Boolean(busy) || !token.trim()} sx={{ height: 40 }}>
            {busy === 'save' ? <CircularProgress size={16} color="inherit" /> : t('city_events_eb_save', { defaultValue: 'Save and test' })}
          </Button>
        </Box>
        {status.keyLast4 ? (
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              sx={{ ml: 0, mb: 1 }}
              control={<Switch size="small" checked={status.enabled === true} disabled={Boolean(busy)} onChange={(e) => setNightly(e.target.checked)} />}
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t('city_events_eb_nightly', { defaultValue: 'Import every night' })}</Typography>}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('city_events_eb_orgs', { defaultValue: 'Every night, import the upcoming events of:' })}</Typography>
            {Object.keys(available).length ? Object.keys(available).map((id) => (
              <FormControlLabel
                key={id} sx={{ display: 'flex', ml: 0 }}
                control={<Checkbox size="small" checked={picked[id] === true} disabled={Boolean(busy)} onChange={(e) => toggleOrg(id, e.target.checked)} />}
                label={available[id]}
              />
            )) : (
              <Typography variant="body2" color="text.secondary">{t('city_events_eb_no_orgs', { defaultValue: 'This Eventbrite account belongs to no organisations, so there is nothing to import at night. Pasting event links in the New event form still works.' })}</Typography>
            )}
          </Box>
        ) : null}
        {error ? <InlineMessage kind="error" sx={{ mt: 2 }}>{error}</InlineMessage> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        {status.keyLast4 ? (
          <Button color="error" onClick={remove} disabled={Boolean(busy)}>
            {busy === 'remove' ? <CircularProgress size={16} /> : t('city_events_eb_remove', { defaultValue: 'Remove key' })}
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {status.keyLast4 ? (
          <Button onClick={test} disabled={Boolean(busy)}>
            {busy === 'test' ? <CircularProgress size={16} /> : t('city_events_eb_test', { defaultValue: 'Test again' })}
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={Boolean(busy)}>{t('close', { defaultValue: 'Close' })}</Button>
      </DialogActions>
    </Dialog>
  );
}

function AutoEventsPanel({ t, dispatch, sources, events, onEdit, onEventsChanged, setSnack }) {
  const phone = usePhone();
  const { auth: firebaseAuth, config } = useContext(FirebaseContext);
  const projectId = (config && config.projectId) || 'donkey-drive-5cdd1';
  const region = directFunctionRegion(config);
  const [running, setRunning] = useState(null); // 'all' | source id
  const [result, setResult] = useState(null);
  const [feedDialog, setFeedDialog] = useState(null); // { name, url, venue }
  const [keyDialog, setKeyDialog] = useState(false);

  useEffect(() => { dispatch(api.fetchCityEventSources()); }, [dispatch]);

  const rows = useMemo(() => {
    const builtins = CONNECTORS.filter((c) => c.builtin).map((c) => Object.assign({}, c, sources[c.id] || {}));
    const feeds = Object.keys(sources).filter((id) => sources[id] && sources[id].kind === 'ics').map((id) => Object.assign({ id, price: 'free', feed: true }, sources[id]));
    // Key-managed connectors (Eventbrite): the registry decides what the row
    // is, the stored row adds the key status, the switch and the run stats.
    const keyed = CONNECTORS.filter((c) => c.keyed).map((c) => Object.assign({}, sources[c.id] || {}, { id: c.id, kind: c.id, name: c.name, price: c.price, desc: c.desc, requires: c.requires, keyed: true, designed: false }));
    const designed = CONNECTORS.filter((c) => c.designed).map((c) => Object.assign({}, c, sources[c.id] || {}, { enabled: false }));
    return builtins.concat(feeds, keyed, designed);
  }, [sources]);

  const run = async (only) => {
    if (running) return;
    setRunning(only);
    setResult(null);
    try {
      const res = await callDirectFunction(firebaseAuth, projectId, region, 'runCityEventSources', { only });
      setResult(res);
      await dispatch(api.fetchCityEventSources());
      if (onEventsChanged) onEventsChanged();
    } catch (err) {
      setSnack({ severity: 'error', msg: err.message });
    } finally {
      setRunning(null);
    }
  };
  const toggle = async (row, on) => {
    const res = await dispatch(api.saveCityEventSource(row.id, { kind: row.kind || row.id, name: row.name, enabled: on }));
    if (!res.success) setSnack({ severity: 'error', msg: res.error });
  };
  const saveFeed = async () => {
    const f = feedDialog;
    if (!f || !f.name.trim() || !/^https?:\/\/\S+/i.test(f.url.trim())) { setSnack({ severity: 'error', msg: t('city_events_feed_invalid', { defaultValue: 'Give the feed a name and an https:// URL.' }) }); return; }
    const venue = f.venue ? { name: f.venue.en, nameAm: f.venue.am || null, lat: f.venue.lat, lng: f.venue.lng } : null;
    const res = await dispatch(api.saveCityEventSource(null, { name: f.name.trim(), url: f.url.trim(), venue, enabled: true }));
    if (res.success) { setFeedDialog(null); setSnack({ severity: 'success', msg: t('city_events_feed_saved', { defaultValue: 'Feed saved.' }) }); }
    else setSnack({ severity: 'error', msg: res.error });
  };
  const removeFeed = async (row) => {
    const ok = await confirmDialog({
      title: t('city_events_confirm_delete_feed', { defaultValue: 'Remove this feed? Events it already created stay in the list.' }),
      confirmLabel: t('city_events_btn_delete_feed', { defaultValue: 'Remove feed' }),
      destructive: true,
    });
    if (!ok) return;
    const res = await dispatch(api.deleteCityEventSource(row.id));
    if (!res.success) setSnack({ severity: 'error', msg: res.error });
  };
  const lastRunLabel = (row) => {
    if (row.designed) return t('city_events_designed', { defaultValue: 'Designed · disabled' });
    if (!row.lastRun) return t('city_events_never_ran', { defaultValue: 'Never ran' });
    return t('city_events_ran_at', { when: fmtDayTime(row.lastRun), defaultValue: 'Ran {{when}}' });
  };
  const resultLabel = (row) => {
    const r = row.lastResult;
    if (!r || row.designed) return '—';
    if (r.error) return '⚠ ' + r.error;
    if (r.status === 'needs_credentials') return t('city_events_eb_needs_key', { defaultValue: 'Needs a key' });
    if (r.status === 'no_organisations') return t('city_events_eb_no_orgs_ticked', { defaultValue: 'No organisations ticked' });
    if (Array.isArray(r.errors) && r.errors.length) {
      const created0 = Number(r.created || 0);
      return (created0 ? created0 + ' new · ' : '') + '⚠ ' + t('city_events_eb_orgs_failed', { failed: r.errors.length, total: r.organisations || r.errors.length, defaultValue: '{{failed}} of {{total}} organisations failed' });
    }
    const created = Number(r.created || 0);
    return created ? created + ' new' : (Number(r.candidates || 0) + ' seen, nothing new');
  };
  const nowMs = Date.now();
  const builtinEvents = useMemo(() => events
    .filter((ev) => ev.source && (ev.source.kind === 'calendar' || ev.source.kind === 'weekly') && Number(ev.end) >= nowMs && Number(ev.start) <= nowMs + 60 * 864e5)
    .slice(0, 60), [events, nowMs]);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" color="neutral" startIcon={<RssFeedIcon />} onClick={() => setFeedDialog({ name: '', url: '', venue: null })}>
          {t('city_events_add_ics', { defaultValue: 'Add ICS feed' })}
        </Button>
        <Button variant="contained" startIcon={running === 'all' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />} disabled={Boolean(running)} onClick={() => run('all')}>
          {t('city_events_run_all', { defaultValue: 'Run all sources now' })}
        </Button>
      </Stack>
      {result ? (
        <InlineMessage kind={result.errors && result.errors.length ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setResult(null)}>
          {t('city_events_run_result', { created: result.created || 0, published: result.published || 0, pending: result.pending || 0, skipped: (result.skipped ? (result.skipped.existingRef || 0) + (result.skipped.duplicate || 0) + (result.skipped.deferred || 0) : 0), defaultValue: '{{created}} new ({{published}} published, {{pending}} to review), {{skipped}} skipped' })}
          {result.errors && result.errors.length ? <Typography variant="caption" display="block">{result.errors.join(' · ')}</Typography> : null}
        </InlineMessage>
      ) : null}
      <Paper sx={{ overflowX: 'auto' }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('city_events_connectors', { defaultValue: 'Connectors' })}</Typography>
          <Typography variant="body2" color="text.secondary">{t('city_events_connectors_hint', { defaultValue: 'Nightly at 2:40 am Addis while the auto-sources switch is on. The built-in calendar publishes itself; every other source lands in the review queue. Paid connectors are designed but switched off until a key is added.' })}</Typography>
        </Box>
        <Table size="small">
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ width: 60 }}>
                  <Switch size="small" checked={row.keyed ? row.enabled === true : (row.enabled !== false && !row.designed)} disabled={Boolean(row.designed) || (row.keyed && !row.keyLast4)} onChange={(e) => toggle(row, e.target.checked)} inputProps={{ 'aria-label': row.name }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name} <Chip size="small" label={row.price} sx={{ ml: 1 }} /></Typography>
                  <Typography variant="caption" color="text.secondary">{row.feed ? row.url : (row.keyed ? t('city_events_eb_desc', row.desc) : row.desc)}</Typography>
                  {row.keyed ? (
                    <Typography variant="caption" display="block" sx={{ color: row.keyError ? 'error.main' : 'text.secondary' }}>
                      {!row.keyLast4 ? t('city_events_eb_key_none', { defaultValue: 'No key yet.' }) : (row.keyError
                        ? t('city_events_eb_key_error', { error: row.keyError, defaultValue: 'Key problem: {{error}}' })
                        : t('city_events_eb_key_saved', { last4: row.keyLast4, account: (row.account && row.account.name) || '—', when: row.verifiedAt ? fmtDayTime(row.verifiedAt) : '—', defaultValue: 'Key ending {{last4}} · {{account}} · checked {{when}}' }))}
                    </Typography>
                  ) : null}
                  {row.feed && row.venue && row.venue.name ? <Typography variant="caption" color="text.secondary" display="block">{t('city_events_feed_default', { venue: row.venue.name, defaultValue: 'Default venue: {{venue}}' })}</Typography> : null}
                  {row.designed && row.requires ? <Typography variant="caption" color="text.secondary" display="block">{t('city_events_needs_key', { list: row.requires.join(', '), defaultValue: 'Needs credentials: {{list}}' })}</Typography> : null}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{lastRunLabel(row)}</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Tooltip title={row.lastResult && (row.lastResult.error || (Array.isArray(row.lastResult.errors) && row.lastResult.errors.join(' · '))) ? (row.lastResult.error || row.lastResult.errors.join(' · ')) : ''}>
                    <Typography variant="body2" noWrap>{resultLabel(row)}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {row.feed ? <Tooltip title={t('city_events_btn_delete_feed', { defaultValue: 'Remove feed' })}><IconButton size="small" onClick={() => removeFeed(row)}><DeleteOutlineIcon /></IconButton></Tooltip> : null}
                  {row.keyed ? (
                    <Button size="small" startIcon={<VpnKeyIcon />} onClick={() => setKeyDialog(true)} sx={{ mr: 0.5 }}>
                      {t('city_events_eb_manage', { defaultValue: 'Manage key' })}
                    </Button>
                  ) : null}
                  <Button size="small" disabled={Boolean(running) || Boolean(row.designed) || row.enabled === false || (row.keyed && (!row.keyLast4 || row.enabled !== true))} onClick={() => run(row.id)}>
                    {running === row.id ? <CircularProgress size={14} /> : t('city_events_run_now', { defaultValue: 'Run now' })}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length ? <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t('city_events_no_sources', { defaultValue: 'No sources yet — run all sources once to seed the built-in calendar rows.' })}</Typography></TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </Paper>
      <Paper sx={{ mt: 2, overflowX: 'auto' }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('city_events_builtin_title', { defaultValue: 'Built-in calendar · next 60 days' })}</Typography>
          <Typography variant="body2" color="text.secondary">{t('city_events_builtin_hint', { defaultValue: 'Generated and auto-published, editable like any event (change the venue or window and it stays changed; cancel it and it is never re-created).' })}</Typography>
        </Box>
        <Table size="small">
          <TableBody>
            {builtinEvents.map((ev) => (
              <TableRow key={ev.id} hover sx={{ cursor: 'pointer' }} onClick={() => onEdit(ev)}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(ev.start)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.title && ev.title.en}</Typography>
                  <Typography variant="caption" color="text.secondary">{(ev.venue && ev.venue.name) || ''} · {ev.source.kind === 'weekly' ? 'weekly' : 'calendar'} · {fmtTime(ev.start)} – {fmtTime(ev.end)}</Typography>
                </TableCell>
                <TableCell align="right">{statusChip(ev.status, ev.status === STATUS.PUBLISHED ? 'auto' : ev.status)}</TableCell>
              </TableRow>
            ))}
            {!builtinEvents.length ? <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t('city_events_builtin_empty', { defaultValue: 'Nothing generated for the next 60 days yet.' })}</Typography></TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </Paper>
      {keyDialog ? (
        <EventbriteKeyDialog
          t={t} row={rows.find((r) => r.id === 'eventbrite')} onClose={() => setKeyDialog(false)}
          firebaseAuth={firebaseAuth} projectId={projectId} region={region} dispatch={dispatch} setSnack={setSnack}
        />
      ) : null}
      {feedDialog ? (
        <Dialog open onClose={() => setFeedDialog(null)} maxWidth="sm" fullWidth fullScreen={phone}>
          <DialogTitle>{t('city_events_add_ics', { defaultValue: 'Add ICS feed' })}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label={t('city_events_feed_name', { defaultValue: 'Feed name' })} value={feedDialog.name} onChange={(e) => setFeedDialog({ ...feedDialog, name: e.target.value })} fullWidth autoFocus />
              <TextField label={t('city_events_feed_url', { defaultValue: 'ICS URL (https://…)' })} value={feedDialog.url} onChange={(e) => setFeedDialog({ ...feedDialog, url: e.target.value })} fullWidth />
              <Autocomplete options={PLACES} getOptionLabel={(o) => o.en + (o.am ? ' · ' + o.am : '')} value={feedDialog.venue} onChange={(e, v) => setFeedDialog({ ...feedDialog, venue: v })}
                renderInput={(params) => <TextField {...params} label={t('city_events_feed_venue', { defaultValue: 'Default venue (used when the feed gives no known location)' })} />} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFeedDialog(null)}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
            <Button variant="contained" onClick={saveFeed}>{t('save', { defaultValue: 'Save' })}</Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Impact (phase 5). functions/common/cityEventsImpact.js writes
// /city_events/{id}/impact once an event's demand span has ended (nightly
// 03:10 Addis): requests (app bookings + driver street hails, the Hot zones
// counters) in the cells around the venue against the same weekday-hours of
// the previous baseline weeks, per window, plus the same comparison over the
// whole city. Supervisors + admins read it here; Recompute calls
// computeCityEventImpact for one event (an event still running → partial).
// ─────────────────────────────────────────────────────────────────────────────
const VERDICT_STYLE = {
  strong: { tone: 'success', icon: <TrendingUpRoundedIcon /> },
  some: { tone: 'info', icon: <TrendingUpRoundedIcon /> },
  none: { tone: 'neutral', icon: <TrendingFlatRoundedIcon /> },
  below: { tone: 'warning', icon: <TrendingDownRoundedIcon /> },
  nodata: { tone: 'neutral', icon: <HelpOutlineRoundedIcon /> },
};
const pctOf = (ratio) => (ratio === null || ratio === undefined || !Number.isFinite(Number(ratio)) ? null : Math.round((Number(ratio) - 1) * 100));
const fmtPct = (ratio) => { const p = pctOf(ratio); return p === null ? '—' : (p > 0 ? '+' : '') + p + '%'; };
const n1 = (v) => (v !== null && v !== undefined && Number.isFinite(Number(v)) ? String(Math.round(Number(v) * 10) / 10) : '—');

function WindowCell({ w, strong }) {
  const { t } = useTranslation();
  if (!w || typeof w !== 'object') return <TableCell sx={{ color: 'text.disabled' }}>—</TableCell>;
  const p = pctOf(w.ratio);
  const color = p === null ? 'text.secondary' : p >= 20 ? 'success.main' : p <= -20 ? 'warning.main' : 'text.secondary';
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      <Typography variant="body2" sx={{ fontWeight: strong ? 600 : 400 }}>
        {n1(w.actual)} <Typography component="span" variant="caption" color="text.secondary">{t('city_events_vs_usual', { n: n1(w.usual), defaultValue: 'vs {{n}}' })}</Typography>
      </Typography>
      <Typography variant="caption" sx={{ color }}>{fmtPct(w.ratio)}</Typography>
    </TableCell>
  );
}

function ImpactHours({ t, im }) {
  const hours = Array.isArray(im.hours) ? im.hours.filter((h) => Array.isArray(h) && h.length >= 3) : [];
  const max = hours.reduce((m, h) => Math.max(m, Number(h[1]) || 0, Number(h[2]) || 0), 0) || 1;
  if (!hours.length) return <Typography variant="caption" color="text.secondary">{t('city_events_impact_no_hours', { defaultValue: 'No hourly detail stored.' })}</Typography>;
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="caption" color="text.secondary">{t('city_events_impact_hours_hint', { defaultValue: 'Requests per hour around the venue — this event (orange) against the usual for that weekday hour (grey).' })}</Typography>
      <Table size="small" sx={{ width: 'auto', mt: 0.5 }}>
        <TableBody>
          {hours.map((h) => (
            <TableRow key={h[0]}>
              <TableCell sx={{ border: 0, py: 0.25, whiteSpace: 'nowrap' }}>{fmtHour(h[0])}</TableCell>
              <TableCell sx={{ border: 0, py: 0.25, width: 260 }}>
                <Box sx={{ height: 8, width: (100 * (Number(h[1]) || 0) / max) + '%', bgcolor: c.ACCENT, borderRadius: 1, minWidth: 2 }} />
                <Box sx={{ height: 8, width: (100 * (Number(h[2]) || 0) / max) + '%', bgcolor: c.OUTLINE_STRONG, borderRadius: 1, mt: 0.25, minWidth: 2 }} />
              </TableCell>
              <TableCell sx={{ border: 0, py: 0.25, whiteSpace: 'nowrap' }}>
                <Typography variant="caption">{n1(h[1])} <Typography component="span" variant="caption" color="text.secondary">{t('city_events_vs_usual', { n: n1(h[2]), defaultValue: 'vs {{n}}' })}</Typography></Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ImpactPanel({ t, events, onRefresh, setSnack }) {
  const { auth: firebaseAuth, config } = useContext(FirebaseContext);
  const projectId = (config && config.projectId) || 'donkey-drive-5cdd1';
  const region = directFunctionRegion(config);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);
  const nowMs = Date.now();
  const rows = useMemo(() => events
    .filter((ev) => (ev.status === STATUS.PUBLISHED || ev.status === STATUS.CANCELLED) && Number(ev.end) < nowMs)
    .slice().sort((a, b) => Number(b.end) - Number(a.end)), [events, nowMs]);
  const reported = rows.filter((ev) => ev.impact && typeof ev.impact === 'object' && ev.impact.verdict && ev.impact.partial !== true);
  const hasCell = (ev) => Boolean(ev.venue && ev.venue.cell);
  // Denominator: published events the nightly can measure (venue inside the
  // grid) plus any cancelled one somebody measured on demand.
  const measurable = rows.filter((ev) => (ev.status === STATUS.PUBLISHED && hasCell(ev)) || (ev.impact && typeof ev.impact === 'object' && ev.impact.verdict));
  const lifted = reported.filter((ev) => ev.impact.verdict === 'strong' || ev.impact.verdict === 'some');
  // The next nightly (3:10 am Addis) reads the day index of its own day and
  // the 3 days before; an event is indexed under its first days (start … end,
  // at most 3), so it is reachable while its LAST index day is in that range.
  const today310 = (() => { const p = api.msToAddisParts(nowMs); return api.addisPartsToMs({ y: p.y, m: p.m, d: p.d, h: 3, mi: 10 }); })();
  const nextRun = nowMs < today310 ? today310 : today310 + 864e5;
  const nightlyFloor = api.addisDayKey(nextRun - 3 * 864e5);
  const lastIndexDay = (ev) => { const days = api.cityEventDayKeys(ev); return days.length ? days[days.length - 1] : api.addisDayKey(Number(ev.end)); };
  const pendingCaption = (ev) => {
    if (!hasCell(ev)) return t('city_events_impact_no_cell', { defaultValue: 'Venue outside the Hot zones grid — nothing to measure' });
    if (ev.status === STATUS.CANCELLED) return t('city_events_impact_cancelled', { defaultValue: 'Cancelled — measured only on demand: press Recompute' });
    if (lastIndexDay(ev) < nightlyFloor) return t('city_events_impact_old', { defaultValue: 'Not computed — press Recompute' });
    return t('city_events_impact_pending', { defaultValue: 'Not computed yet — nightly at 3:10 am' });
  };
  const pushed = rows.reduce((acc, ev) => acc + (ev.push && Number(ev.push.sent) > 0 ? Number(ev.push.sent) : 0), 0);
  const ratios = reported
    .map((ev) => (ev.impact.windows && ev.impact.windows.span ? ev.impact.windows.span.ratio : null))
    .filter((r) => r !== null && r !== undefined && Number.isFinite(Number(r))).map(Number).sort((a, b) => a - b);
  const median = ratios.length ? ratios[Math.floor(ratios.length / 2)] : null;
  const verdictLabel = (v) => t('city_events_impact_verdict_' + v, { strong: 'Strong lift', some: 'Some lift', none: 'No change', below: 'Below usual', nodata: 'Not enough data' }[v] || v);
  const recompute = async (ev) => {
    if (busy) return;
    setBusy(ev.id);
    try {
      const res = await callDirectFunction(firebaseAuth, projectId, region, 'computeCityEventImpact', { id: ev.id });
      setSnack(res && res.kept
        ? { severity: 'info', msg: t('city_events_impact_kept', { n: Number(res.impact && res.impact.weeks) || 0, defaultValue: 'Kept the nightly report ({{n}} baseline weeks): the older baseline weeks have been cleared since, a recompute now would be less accurate.' }) }
        : { severity: 'success', msg: t('city_events_impact_done', { defaultValue: 'Impact recomputed.' }) });
      if (onRefresh) onRefresh();
    } catch (err) {
      setSnack({ severity: 'error', msg: err.message });
    } finally {
      setBusy(null);
    }
  };
  const stat = (key, label, value, hint) => (
    <DashboardCard key={key} title={label} value={String(value)} caption={hint || undefined} />
  );
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'grid', gap: 1.5, mb: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
        {stat('events', t('city_events_impact_stat_events', { defaultValue: 'Events with a report' }), reported.length + ' / ' + measurable.length, t('city_events_impact_stat_events_hint', { defaultValue: 'published events of the last 14 days that have ended' }))}
        {stat('lift', t('city_events_impact_stat_lift', { defaultValue: 'With a lift' }), lifted.length, t('city_events_impact_stat_lift_hint', { defaultValue: 'some or strong, whole event' }))}
        {stat('median', t('city_events_impact_stat_median', { defaultValue: 'Typical change' }), median === null ? '—' : fmtPct(median), t('city_events_impact_stat_median_hint', { defaultValue: 'median vs usual, whole event' }))}
        {stat('pushed', t('city_events_impact_stat_pushed', { defaultValue: 'Drivers pushed' }), pushed, t('city_events_impact_stat_pushed_hint', { defaultValue: 'across these events' }))}
      </Box>
      <Paper sx={{ overflowX: 'auto' }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('city_events_impact_title', { defaultValue: 'Did the crowd turn into requests?' })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('city_events_impact_hint', { defaultValue: 'Requests = app bookings + driver street hails counted in the cells around the venue (3 × 3 cells ≈ 1.5 km; 5 × 5 for tens of thousands) during the event\'s arrival, during and exit windows, against the same weekday-hours of the previous weeks (the demand-map baseline). "City" is the same comparison over the whole city, so a holiday that lifts everything reads differently from a venue spike. Computed nightly at 3:10 am for events that have ended; Recompute runs it now (an event still running gets a partial figure).' })}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36 }} />
              <TableCell>{t('city_events_col_when', { defaultValue: 'When (Addis)' })}</TableCell>
              <TableCell>{t('city_events_col_event', { defaultValue: 'Event' })}</TableCell>
              <TableCell>{t('city_events_impact_col_pushed', { defaultValue: 'Pushed' })}</TableCell>
              <TableCell>{t('city_events_impact_col_arrival', { defaultValue: 'Arrival' })}</TableCell>
              <TableCell>{t('city_events_impact_col_during', { defaultValue: 'During' })}</TableCell>
              <TableCell>{t('city_events_impact_col_exit', { defaultValue: 'Exit' })}</TableCell>
              <TableCell>{t('city_events_impact_col_span', { defaultValue: 'Whole event' })}</TableCell>
              <TableCell>{t('city_events_impact_col_city', { defaultValue: 'City' })}</TableCell>
              <TableCell>{t('city_events_impact_col_verdict', { defaultValue: 'Verdict' })}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length ? (
              <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t('city_events_impact_empty', { defaultValue: 'No published event has ended in the last 14 days.' })}</Typography></TableCell></TableRow>
            ) : null}
            {rows.map((ev) => {
              const im = ev.impact && typeof ev.impact === 'object' ? ev.impact : null;
              const w = im && im.windows && typeof im.windows === 'object' ? im.windows : {};
              const isOpen = open === ev.id;
              return (
                <React.Fragment key={ev.id}>
                  <TableRow hover>
                    <TableCell sx={{ width: 36 }}>
                      {im ? <IconButton size="small" onClick={() => setOpen(isOpen ? null : ev.id)}>{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton> : null}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtWhen(ev)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.title && ev.title.en}</Typography>
                      <Typography variant="caption" color="text.secondary">{(ev.venue && ev.venue.name) || ''}{ev.status === STATUS.CANCELLED ? ' · ' + t('city_events_status_cancelled', { defaultValue: 'Cancelled' }) : ''}</Typography>
                    </TableCell>
                    <TableCell>{ev.push && Number(ev.push.sent) > 0 ? Number(ev.push.sent) : '—'}</TableCell>
                    <WindowCell w={w.arrival} />
                    <WindowCell w={w.during} />
                    <WindowCell w={w.exit} />
                    <WindowCell w={w.span} strong />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{im && w.span ? fmtPct(w.span.cityRatio) : '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {im ? (
                        <>
                          <StatusChip tone={(VERDICT_STYLE[im.verdict] || VERDICT_STYLE.nodata).tone} icon={(VERDICT_STYLE[im.verdict] || VERDICT_STYLE.nodata).icon} label={verdictLabel(im.verdict)} />
                          {im.partial ? <Typography variant="caption" color="text.secondary" display="block">{t('city_events_impact_partial', { defaultValue: 'partial — the event was still running' })}</Typography> : null}
                          {!im.partial && im.weeks !== undefined ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('city_events_impact_weeks', { n: Number(im.weeks) || 0, defaultValue: '{{n}} baseline weeks' })}{im.truncated ? ' · ' + t('city_events_impact_truncated', { defaultValue: 'first 18 h' }) : ''}
                            </Typography>
                          ) : null}
                        </>
                      ) : <Typography variant="caption" color="text.secondary">{pendingCaption(ev)}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={t('city_events_impact_recompute', { defaultValue: 'Recompute now' })}>
                        <span><IconButton size="small" disabled={Boolean(busy)} onClick={() => recompute(ev)}>{busy === ev.id ? <CircularProgress size={16} /> : <ReplayIcon />}</IconButton></span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {im ? (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit><ImpactHours t={t} im={im} /></Collapse>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

const statusLine = (t, ev) => {
  const parts = [];
  if (ev.createdByName) parts.push(t('city_events_by', { name: ev.createdByName, defaultValue: 'by {{name}}' }) + (ev.createdAt ? ' · ' + fmtShortDay(ev.createdAt) + ' ' + fmtTime(ev.createdAt) : ''));
  if (ev.status === STATUS.PUBLISHED && ev.approvedByName) parts.push(t('city_events_status_published', { defaultValue: 'Published' }) + ' · ' + ev.approvedByName);
  if (ev.status === STATUS.REJECTED && ev.rejectedReason) parts.push(t('city_events_status_rejected', { defaultValue: 'Rejected' }) + ': ' + ev.rejectedReason);
  return parts.join('  ·  ');
};
